import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { LogOut, Globe, UserIcon, KeyRound } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '@/lib/store';
import { logoutUser } from '@/lib/authSlice';
import { clearPageHistory } from '@/utils/hooks/usePageHistory';
import { useTheme } from '@/providers/theme';
import { setLanguage } from '@/lib/langSlice';
import i18n from '@/locales';
import { LANGUAGES, type Language } from '@/components/LanguageSwitcher';
import CustomButton from '@/components/CustomButton';
import { THEME_CONFIG } from '@/utils/constants/theme';
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toastManager } from '@/components/ui/toast';
import { authService, ChangePasswordData } from '@/services/authService';
import ChangePasswordDialog from '@/components/common/ChangePasswordDialog';
import { useTranslation } from 'react-i18next';

export function UserMenu() {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const { theme, setTheme } = useTheme();
  const locale = useSelector((state: RootState) => state.lang.language);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [changePasswordDialogOpen, setChangePasswordDialogOpen] =
    useState(false);

  const changePasswordMutation = useMutation({
    mutationFn: (data: ChangePasswordData) => authService.changePassword(data),
    onSuccess: () => {
      toastManager.add({
        title: t('userMenu.changePassword.success'),
        type: 'success',
      });
      setChangePasswordDialogOpen(false);
    },
    onError: (error: any) => {
      const errorMessage =
        error?.data?.error?.message ||
        error?.message ||
        t('userMenu.changePassword.error');
      toastManager.add({
        title: errorMessage,
        type: 'error',
      });
    },
  });

  const handleLogout = () => {
    dispatch(logoutUser());
    clearPageHistory();
  };

  const handleLanguageChange = (lang: Language) => {
    dispatch(setLanguage(lang));
    i18n.changeLanguage(lang);
  };

  const handleChangePassword = (
    newPassword: string,
    currentPassword: string
  ) => {
    changePasswordMutation.mutate({
      newPassword,
      currentPassword,
    });
  };

  const currentThemeConfig = THEME_CONFIG.find(config => config.key === theme);
  const ThemeIcon = currentThemeConfig?.icon;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <CustomButton icon={<UserIcon />} size="icon" variant="outline" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" side="top" sideOffset={0}>
          <div className="flex items-center gap-2 p-1">
            <Avatar>
              <AvatarImage src={user?.avatar} alt={user?.firstname} />
              <AvatarFallback>{user?.firstname[0]}</AvatarFallback>
            </Avatar>
            <span className="line-clamp-1 text-sm">
              {`${user?.firstname} ${user?.lastname}`.trim()}
            </span>
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => setProfileDialogOpen(true)}
            className="h-9 px-2"
          >
            <div className="flex items-center gap-2">
              <UserIcon className="h-4 w-4" />
              <span>{t('userMenu.profile')}</span>
            </div>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="h-9 px-2">
              <div className="flex items-center gap-2">
                {ThemeIcon && <ThemeIcon className="h-4 w-4" />}
                <span>{t('userMenu.theme')}</span>
              </div>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {THEME_CONFIG.map(config => {
                const isActive = theme === config.key;
                const Icon = config.icon;
                return (
                  <DropdownMenuItem
                    key={config.key}
                    onClick={() => setTheme(config.key)}
                    className={isActive ? 'bg-muted' : ''}
                  >
                    <Icon className="mr-2 h-4 w-4" />
                    <span>{t(config.labelKey)}</span>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuSubContent>
          </DropdownMenuSub>

          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="h-9 px-2">
              <div className="flex items-center gap-2">
                <Globe />
                <span>{t('userMenu.language')}</span>
              </div>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {Object.entries(LANGUAGES).map(([key, lang]) => (
                <DropdownMenuItem
                  key={key}
                  onClick={() => handleLanguageChange(key as Language)}
                  className={locale === key ? 'bg-muted' : ''}
                >
                  <span className="mr-2 text-base">{lang.flag}</span>
                  <span>{lang.name}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>

          <DropdownMenuSeparator />
          <CustomButton
            icon={<LogOut />}
            onClick={handleLogout}
            variant="destructive"
            label={t('userMenu.logout')}
            className="w-full"
          />
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Profile Dialog - Parent Dialog */}
      <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
        <DialogPopup className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('userMenu.profileDialog.title')}</DialogTitle>
            <DialogDescription>
              {t('userMenu.profileDialog.description')}
            </DialogDescription>
          </DialogHeader>
          <DialogPanel>
            <div className="grid gap-4 py-4">
              <div className="grid gap-1">
                <p className="text-muted-foreground text-sm">
                  {t('userMenu.profileDialog.name')}
                </p>
                <p className="text-sm font-medium">
                  {`${user?.firstname} ${user?.lastname}`.trim()}
                </p>
              </div>
              <div className="grid gap-1">
                <p className="text-muted-foreground text-sm">
                  {t('userMenu.profileDialog.email')}
                </p>
                <p className="text-sm font-medium">{user?.email}</p>
              </div>
            </div>
          </DialogPanel>
          <DialogFooter>
            <Dialog
              open={changePasswordDialogOpen}
              onOpenChange={setChangePasswordDialogOpen}
            >
              <DialogTrigger
                render={
                  <CustomButton
                    icon={<KeyRound />}
                    label={t('userMenu.profileDialog.changePassword')}
                    variant="outline"
                  />
                }
              />
              <ChangePasswordDialog
                onSubmit={handleChangePassword}
                onSuccess={() => setChangePasswordDialogOpen(false)}
                isPending={changePasswordMutation.isPending}
              />
            </Dialog>
            <CustomButton
              label={t('userMenu.profileDialog.close')}
              variant="ghost"
              onClick={() => setProfileDialogOpen(false)}
            />
          </DialogFooter>
        </DialogPopup>
      </Dialog>
    </>
  );
}
